import {
  GENESYS_DEFAULT_REGION,
  GENESYS_REGIONS,
  type GenesysIntegration,
  type GenesysIntegrationRequest,
  type GenesysRegion,
  type IntegrationConnectionTestResponse,
} from "@repo/app-types/entities/integration";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import { Size } from "@repo/ui/helpers/helpers";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Input } from "@repo/ui/Inputs/input";
import { Link } from "@repo/ui/Link/index";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useState, type FC } from "react";
import { Dialog, DialogTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import {
  useConnectGenesysIntegrationMutation,
  useSetGenesysIntegrationEnabledMutation,
  useTestGenesysConnectionMutation,
  useUpdateGenesysIntegrationMutation,
} from "../../../api/entities/genesysIntegration.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";

const GENESYS_OAUTH_GUIDE_URL =
  "https://help.genesys.cloud/articles/create-an-oauth-client/";

/**
 * Human labels for the region hosts. Sourced from `GENESYS_REGIONS` so a region
 * added there can never go missing here — an unlabelled host still renders,
 * showing the bare domain.
 */
const genesysRegionLabels: Partial<Record<GenesysRegion, string>> = {
  "mypurecloud.com": "Americas (US East)",
  "use2.us-gov-pure.cloud": "Americas (US East 2, FedRAMP)",
  "usw2.pure.cloud": "Americas (US West)",
  "cac1.pure.cloud": "Americas (Canada)",
  "mxc1.pure.cloud": "Americas (Mexico)",
  "sae1.pure.cloud": "Americas (São Paulo)",
  "mypurecloud.ie": "EMEA (Dublin)",
  "euw2.pure.cloud": "EMEA (London)",
  "mypurecloud.de": "EMEA (Frankfurt)",
  "euc2.pure.cloud": "EMEA (Zurich)",
  "edee1.eusc-pure.cloud": "EU Sovereign Cloud (Germany East)",
  "mec1.pure.cloud": "Middle East (UAE)",
  "aps1.pure.cloud": "Asia Pacific (Mumbai)",
  "apse1.pure.cloud": "Asia Pacific (Singapore)",
  "mypurecloud.com.au": "Asia Pacific (Sydney)",
  "mypurecloud.jp": "Asia Pacific (Tokyo)",
  "apne2.pure.cloud": "Asia Pacific (Seoul)",
  "apne3.pure.cloud": "Asia Pacific (Osaka)",
};

interface GenesysRegionOption {
  id: GenesysRegion;
  label: string;
}

const genesysRegionOptions: readonly GenesysRegionOption[] =
  GENESYS_REGIONS.map((region) => ({
    id: region,
    label: genesysRegionLabels[region]
      ? `${genesysRegionLabels[region]} — ${region}`
      : region,
  }));

interface GenesysConnectionFormProps {
  mode: "connect" | "edit";
  layout?: "inline" | "modal";
  initialClientId: string;
  initialRegion: GenesysRegion;
  isDisabled: boolean;
  onCancel?: () => void;
  onSave: (credentials: GenesysIntegrationRequest) => Promise<boolean>;
}

/**
 * Credential entry, gated on a successful connection test.
 *
 * Saving is deliberately blocked until a test passes: credentials can be valid
 * while the OAuth client has none of the permissions the sync needs, and that
 * failure is otherwise silent — the sync looks connected and imports nothing.
 */
const GenesysConnectionForm: FC<GenesysConnectionFormProps> = (props) => {
  const {
    mode,
    layout = "inline",
    initialClientId,
    initialRegion,
    isDisabled,
    onCancel,
    onSave,
  } = props;
  const isModalLayout = layout === "modal";

  const [clientId, setClientId] = useState(initialClientId);
  const [clientSecret, setClientSecret] = useState("");
  const [region, setRegion] = useState<GenesysRegion>(initialRegion);
  const [isSecretVisible, setIsSecretVisible] = useState(false);
  const [hasAttemptedTest, setHasAttemptedTest] = useState(false);
  const [testResult, setTestResult] =
    useState<IntegrationConnectionTestResponse | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [testConnection, testState] = useTestGenesysConnectionMutation();

  const trimmedClientId = clientId.trim();
  const trimmedClientSecret = clientSecret.trim();
  const clientIdError =
    hasAttemptedTest && !trimmedClientId ? "Enter a client ID." : undefined;
  const clientSecretError =
    hasAttemptedTest && !trimmedClientSecret
      ? "Enter a client secret."
      : undefined;

  const credentials: GenesysIntegrationRequest = {
    client_id: trimmedClientId,
    client_secret: trimmedClientSecret,
    region,
  };

  // Any edit invalidates the previous result: it was about different credentials.
  const invalidateTest = () => {
    setTestResult(null);
    setSaveError(null);
  };

  const handleTest = async () => {
    setHasAttemptedTest(true);
    if (!trimmedClientId || !trimmedClientSecret) return;

    invalidateTest();
    try {
      setTestResult(await testConnection(credentials).unwrap());
    } catch (error) {
      setTestResult({
        status: "failure",
        error: getApiErrorMessage(
          error,
          "Couldn't reach Genesys Cloud — please try again.",
        ),
      });
    }
  };

  const handleSave = async () => {
    setSaveError(null);
    const saved = await onSave(credentials);
    if (saved) {
      setClientSecret("");
      setTestResult(null);
      setHasAttemptedTest(false);
    } else {
      setSaveError(null);
    }
  };

  return (
    <div className="space-y-6">
      {mode === "connect" ? (
        <p className="text-sm text-muted-foreground">
          Need help creating your credentials?{" "}
          <Link href={GENESYS_OAUTH_GUIDE_URL}>
            View the Genesys Cloud OAuth setup guide
          </Link>
          .
        </p>
      ) : null}

      <section>
        <div className="mb-5">
          {mode === "connect" ? (
            <h2 className="text-sm font-semibold text-foreground">
              Connection details
            </h2>
          ) : null}
          <p
            className={twMerge(
              "text-sm leading-5 text-muted-foreground",
              mode === "connect" && "mt-1",
            )}
          >
            Test the credentials before saving to confirm the required Genesys
            permissions are available.
          </p>
        </div>

        <div className={twMerge("space-y-5", !isModalLayout && "max-w-xl")}>
          <Input
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            errorDescription={clientIdError}
            isDisabled={isDisabled}
            label="Client ID"
            onChange={(event) => {
              setClientId(event.target.value);
              invalidateTest();
            }}
            placeholder="Enter your Genesys client ID"
            required
            type="text"
            value={clientId}
          />

          <Input
            autoCapitalize="none"
            autoComplete="new-password"
            autoCorrect="off"
            description={
              mode === "edit"
                ? "Re-enter the client secret to update this connection — Alpharun never sends a stored secret back."
                : undefined
            }
            errorDescription={clientSecretError}
            isDisabled={isDisabled}
            label="Client secret"
            onChange={(event) => {
              setClientSecret(event.target.value);
              invalidateTest();
            }}
            placeholder={
              mode === "edit"
                ? "Enter your client secret again"
                : "Enter your Genesys client secret"
            }
            required
            rightElement={
              <IconButton
                accessibleLabel={
                  isSecretVisible ? "Hide client secret" : "Show client secret"
                }
                icon={
                  isSecretVisible ? (
                    <IconEyeOff className="size-4" />
                  ) : (
                    <IconEye className="size-4" />
                  )
                }
                onClick={() => setIsSecretVisible(!isSecretVisible)}
                size={Size.SMALL}
                variant={ButtonVariant.Tertiary}
              />
            }
            type={isSecretVisible ? "text" : "password"}
            value={clientSecret}
          />

          <ComboboxField<GenesysRegionOption>
            description="Choose the region where your Genesys Cloud organization is hosted."
            getLabel={(option) => option.label}
            isDisabled={isDisabled}
            items={genesysRegionOptions}
            label="Genesys region"
            onChange={(value) => {
              if (value !== null) {
                setRegion(value);
                invalidateTest();
              }
            }}
            placeholder="Select a region"
            value={region}
          />
        </div>
      </section>

      {testResult ? (
        <div className={twMerge(!isModalLayout && "max-w-xl")}>
          {/* The message names what to change — a rejected credential and a
              missing permission need different fixes. */}
          <InfoBanner
            description={
              testResult.status === "failure" ? testResult.error : undefined
            }
            title={
              testResult.status === "success"
                ? "Connection successful"
                : "Couldn't connect to Genesys Cloud"
            }
            variant={
              testResult.status === "success"
                ? SimpleModalVariant.Standard
                : SimpleModalVariant.Warning
            }
          />
        </div>
      ) : null}

      {saveError ? (
        <p
          className={twMerge(
            "text-sm text-red-600",
            !isModalLayout && "max-w-xl",
          )}
        >
          {saveError}
        </p>
      ) : null}

      <div
        className={twMerge(
          "flex flex-wrap items-center justify-end gap-2",
          isModalLayout ? "pt-1" : "max-w-xl border-t border-border-light pt-5",
        )}
      >
        {onCancel ? (
          <Button
            isDisabled={isDisabled}
            onClick={onCancel}
            variant={ButtonVariant.Tertiary}
          >
            Cancel
          </Button>
        ) : null}
        <Button
          isDisabled={isDisabled || testState.isLoading}
          isLoading={testState.isLoading}
          onClick={() => void handleTest()}
          variant={ButtonVariant.Secondary}
        >
          {testResult?.status === "success" ? "Test again" : "Test connection"}
        </Button>
        <Button
          isDisabled={isDisabled || testResult?.status !== "success"}
          onClick={() => void handleSave()}
          variant={ButtonVariant.Primary}
        >
          {mode === "connect" ? "Connect and start syncing" : "Save changes"}
        </Button>
      </div>
    </div>
  );
};

interface GenesysEditConnectionDialogProps {
  integration: GenesysIntegration;
  isDisabled: boolean;
  onSave: (credentials: GenesysIntegrationRequest) => Promise<boolean>;
}

const GenesysEditConnectionDialog: FC<GenesysEditConnectionDialogProps> = (
  props,
) => {
  const { integration, isDisabled, onSave } = props;

  return (
    <DialogTrigger>
      <Button
        className="shrink-0"
        isDisabled={isDisabled}
        size={Size.SMALL}
        variant={ButtonVariant.Secondary}
      >
        Edit connection
      </Button>
      <CenteredModal isDismissable size={Size.LARGE}>
        <Dialog className="px-4 pt-5 pb-4 text-left sm:p-6">
          {({ close }) => (
            <>
              <ModalHeader isCloseVisible>Edit connection</ModalHeader>
              <div className="mt-4">
                <GenesysConnectionForm
                  initialClientId={integration.settings.client_id}
                  initialRegion={integration.settings.region}
                  isDisabled={isDisabled}
                  layout="modal"
                  mode="edit"
                  onCancel={close}
                  onSave={async (credentials) => {
                    const saved = await onSave(credentials);
                    if (saved) close();
                    return saved;
                  }}
                />
              </div>
            </>
          )}
        </Dialog>
      </CenteredModal>
    </DialogTrigger>
  );
};

export interface GenesysIntegrationSettingsProps {
  integration: GenesysIntegration | null;
  isLoading: boolean;
  isError: boolean;
  onReload: () => void;
  onRequestDisconnect: () => void;
}

export const GenesysIntegrationSettings: FC<GenesysIntegrationSettingsProps> = (
  props,
) => {
  const { integration, isLoading, isError, onReload, onRequestDisconnect } =
    props;
  const [connectIntegration, connectState] =
    useConnectGenesysIntegrationMutation();
  const [updateIntegration, updateState] =
    useUpdateGenesysIntegrationMutation();
  const [setEnabled, setEnabledState] =
    useSetGenesysIntegrationEnabledMutation();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isPauseConfirmationOpen, setIsPauseConfirmationOpen] = useState(false);

  const isSaving = connectState.isLoading || updateState.isLoading;

  if (isLoading) {
    return <Loader style={LoaderStyle.SPINNER} />;
  }
  if (isError) {
    return (
      <div className="flex max-w-3xl flex-col items-start gap-3 rounded-lg border border-border-light bg-card p-4">
        <p className="text-sm text-muted-foreground">
          Couldn&apos;t load your Genesys Cloud configuration.
        </p>
        <Button
          onClick={onReload}
          size={Size.SMALL}
          variant={ButtonVariant.Secondary}
        >
          Try again
        </Button>
      </div>
    );
  }

  /** Returns whether the write landed, so the form can clear the secret. */
  const saveConnection = async (
    credentials: GenesysIntegrationRequest,
  ): Promise<boolean> => {
    setSaveError(null);
    try {
      if (integration) {
        await updateIntegration({
          integrationId: integration.id,
          credentials,
        }).unwrap();
      } else {
        await connectIntegration(credentials).unwrap();
      }
      return true;
    } catch (error) {
      setSaveError(
        getApiErrorMessage(
          error,
          "Could not save the Genesys Cloud connection — please try again.",
        ),
      );
      return false;
    }
  };

  const changeEnabled = async (isEnabled: boolean) => {
    if (!integration) return;
    setSaveError(null);
    try {
      await setEnabled({
        integrationId: integration.id,
        isEnabled,
      }).unwrap();
    } catch (error) {
      setSaveError(
        getApiErrorMessage(
          error,
          `Could not ${isEnabled ? "resume" : "pause"} the Genesys sync — please try again.`,
        ),
      );
    }
  };

  if (!integration) {
    return (
      <Can
        I={PermissionActions.CREATE}
        a={PermissionSubjects.INTEGRATIONS}
        passThrough={true}
      >
        {(allowed) =>
          allowed ? (
            <>
              {saveError ? (
                <p className="mb-4 max-w-xl text-sm text-red-600">
                  {saveError}
                </p>
              ) : null}
              <GenesysConnectionForm
                initialClientId=""
                initialRegion={GENESYS_DEFAULT_REGION}
                isDisabled={isSaving}
                mode="connect"
                onSave={saveConnection}
              />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              You don&apos;t have permission to connect integrations.
            </p>
          )
        }
      </Can>
    );
  }

  const isLive = integration.settings.is_enabled;
  // The only timestamp we hold is the discovery high-water mark, so this says
  // what it actually is. There is no "last connected" time to show.
  const syncedThrough = integration.settings.last_conversation_end_at
    ? DateTime.fromISO(
        integration.settings.last_conversation_end_at,
      ).toLocaleString(DateTime.DATETIME_MED)
    : "Nothing synced yet";

  return (
    <div className="space-y-7">
      {saveError ? <p className="text-sm text-red-600">{saveError}</p> : null}

      <section className="rounded-lg border border-border-light bg-card p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className={twMerge(
                "mt-1.5 size-2.5 shrink-0 rounded-full",
                isLive ? "bg-success" : "bg-amber-400",
              )}
            />
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                {isLive ? "Sync is live" : "Sync is paused"}
              </h2>
              <p className="mt-1 max-w-lg text-sm leading-5 text-muted-foreground">
                {isLive
                  ? "Alpharun checks Genesys Cloud for new call recordings every 5 minutes."
                  : "No new call recordings are being imported. Resuming picks up from where the sync stopped — nothing recorded during the pause is lost."}
              </p>
            </div>
          </div>

          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.INTEGRATIONS}
            passThrough={true}
          >
            {(allowed) => (
              <div className="shrink-0 pt-0.5">
                <Button
                  isDisabled={!allowed || setEnabledState.isLoading}
                  isLoading={setEnabledState.isLoading}
                  onClick={() => {
                    if (isLive) {
                      setIsPauseConfirmationOpen(true);
                      return;
                    }
                    void changeEnabled(true);
                  }}
                  size={Size.SMALL}
                  variant={
                    isLive ? ButtonVariant.Secondary : ButtonVariant.Primary
                  }
                >
                  {isLive ? "Pause sync" : "Resume sync"}
                </Button>
              </div>
            )}
          </Can>
        </div>

        <dl className="mt-5 grid gap-4 border-t border-border-light pt-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium text-muted-foreground">
              Sync schedule
            </dt>
            <dd className="mt-1 text-foreground">Every 5 minutes</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted-foreground">
              Synced through
            </dt>
            <dd className="mt-1 text-foreground">{syncedThrough}</dd>
          </div>
        </dl>
      </section>

      <section>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Connection details
            </h2>
            <p className="mt-1 text-sm leading-5 text-muted-foreground">
              Credentials are used only to retrieve recordings from this Genesys
              Cloud organization.
            </p>
          </div>
          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.INTEGRATIONS}
            passThrough={true}
          >
            {(allowed) => (
              <GenesysEditConnectionDialog
                integration={integration}
                isDisabled={!allowed || isSaving}
                onSave={saveConnection}
              />
            )}
          </Can>
        </div>

        <dl className="mt-4 divide-y divide-border-light rounded-lg border border-border-light text-sm">
          <div className="grid gap-1 px-4 py-3 sm:grid-cols-[11rem_1fr] sm:gap-4">
            <dt className="text-muted-foreground">Client ID</dt>
            <dd className="min-w-0 truncate font-mono text-xs text-foreground">
              {integration.settings.client_id}
            </dd>
          </div>
          <div className="grid gap-1 px-4 py-3 sm:grid-cols-[11rem_1fr] sm:gap-4">
            <dt className="text-muted-foreground">Client secret</dt>
            {/* A fixed mask, not the stored value: the secret is write-only and
                never leaves the server, so there is nothing here to reveal. */}
            <dd className="font-mono text-xs tracking-widest text-foreground">
              ••••••••••••••••
            </dd>
          </div>
          <div className="grid gap-1 px-4 py-3 sm:grid-cols-[11rem_1fr] sm:gap-4">
            <dt className="text-muted-foreground">Genesys region</dt>
            <dd className="text-foreground">
              {genesysRegionLabels[integration.settings.region]
                ? `${genesysRegionLabels[integration.settings.region]} — ${integration.settings.region}`
                : integration.settings.region}
            </dd>
          </div>
        </dl>
      </section>

      <section className="border-t border-border-light pt-6">
        <h2 className="text-sm font-semibold text-foreground">
          How calls are handled
        </h2>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">
          Agents are matched to Alpharun teammates using their Genesys profile
          email. Existing playbook routing rules decide where imported calls are
          sent.
        </p>
      </section>

      <section className="flex flex-col gap-3 border-t border-border-light pt-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">
            Disconnect Genesys Cloud
          </h2>
          <p className="mt-1 text-sm leading-5 text-muted-foreground">
            This stops new call recordings from syncing to Alpharun.
          </p>
        </div>
        <Can
          I={PermissionActions.DELETE}
          a={PermissionSubjects.INTEGRATIONS}
          passThrough={true}
        >
          {(allowed) => (
            <Button
              isDisabled={!allowed}
              onClick={onRequestDisconnect}
              size={Size.SMALL}
              variant={ButtonVariant.Warning}
            >
              Disconnect
            </Button>
          )}
        </Can>
      </section>

      <SimpleModal
        confirmButtonText="Pause sync"
        isOpen={isPauseConfirmationOpen}
        onCancel={() => setIsPauseConfirmationOpen(false)}
        onConfirm={() => {
          void changeEnabled(false);
          setIsPauseConfirmationOpen(false);
        }}
        // Resuming genuinely does backfill the paused stretch: the sync tracks
        // how far it has read, not the wall clock. It catches up in bounded
        // steps, so a long pause takes a while to work through the backlog.
        subtitle="Call recordings created while the sync is paused are imported when you resume, catching up gradually rather than all at once."
        title="Pause Genesys sync?"
        variant={SimpleModalVariant.Warning}
      />
    </div>
  );
};
